import { isBehindSchedule, isDueThisWeek } from './forecast-status-pills'
import { isBooked } from './forecast-queue-groups'
import type { StatusPillRow } from './forecast-status-pills'

/**
 * The two firm goals, as progress bars across the top of the Forecasts console
 * (sc-1212).
 *
 * Tyler, 2026-10-20: *"slim cards across the top full width that light up when
 * achieved… so this can be a goal progress bar actually that fills and unfills
 * to show progress… Then we can have a dotted vertical line of 80% to try and
 * hold to target."* That was one shared line for both bars. **2026-10-21: Tyler
 * raised Booked with room to 90%** (*"for Target 80% on booked with room change
 * that copy to target 90%"*), so the target is now per goal and Started in time
 * keeps 80%. The reason is measurement: Booked with room read 58 of 64 = 91% on
 * production that day, so against an 80% line it could essentially never fail
 * and a goal nobody can miss stops being read. At 90% it passes by ONE case (a
 * single extra short-notice booking makes it 57 of 64 = 89% and the light goes
 * out), which is a live target. Started in time, at 44%, needs no such change.
 *
 * ## Two bars, not one gated bar
 *
 * It started as a single bar whose second half unlocked only once the first was
 * full — *"we have to hit the first 50% to ever achieve the last"*. Measuring
 * it killed that shape: **booking lead is already met on 95% of the board**, so
 * the first half would sit just shy of complete forever, two short-notice
 * bookings would hold it hostage, and the second goal could never unlock. Tyler
 * then: *"Well then make them 2 cards seperate goals?"* — which is what this is.
 * Each bar moves on its own.
 *
 * ## What each one measures, and why neither needed new arithmetic
 *
 * Both are complements of rules already shipped and tested, which is the point:
 * a goal that disagreed with the row it summarises would be worse than no goal.
 *
 * - **Booked with room** — the share of booked cases NOT due inside a week.
 *   The exact complement of {@link isDueThisWeek}, the rule behind the Due this
 *   week card. "A week out" is Tyler's own phrase for it.
 * - **Started in time** — the share of booked cases NOT behind schedule. The
 *   complement of {@link isBehindSchedule}, so this bar and the Behind schedule
 *   card can never disagree about the same case. It answers *"if we start
 *   tickets on or before the latest safe start date we get filled up but more
 *   we get behind it unfills."*
 *
 * ## Denominator: booked cases only
 *
 * A case with no delivery meeting has no deadline to have room before and no
 * safe start to beat, so including it would move a goal by booking a meeting
 * rather than by doing anything better. 62 of the 77 non-Annual-Review open
 * rows are booked.
 *
 * ## Measured on production, 2026-10-20
 *
 * Booked with room **95%** (59 of 62) — comfortable against the original 80%
 * line, which is why it was later raised to 90% (see above). Started in time **44%** (27 of 62) — the real one, and
 * the same cadence finding that runs through this whole story: the median
 * booked runway is 19 business days against work that needs about 19, so cases
 * are late to start the day they are booked.
 */

/**
 * The DEFAULT hold-to line, 80, and Started in time's own target.
 *
 * Tyler's original number: *"a dotted vertical line of 80% to try and hold to
 * target."* A hold-to line rather than a finish line — 100% needs every case to
 * behave, which one short-notice booking denies, and a goal nobody can ever
 * reach stops being read.
 *
 * **No longer what anything reads to draw or judge a bar.** Each goal carries
 * its own {@link ForecastGoal.targetPct}, copied onto its reading, because the
 * two goals no longer share a line.
 */
export const GOAL_TARGET_PCT = 80

export type ForecastGoalKey = 'BOOKED_WITH_ROOM' | 'STARTED_IN_TIME'

export interface ForecastGoal {
  key: ForecastGoalKey
  label: string
  /** One line under the bar, saying what would move it. */
  description: string
  /**
   * What the cases the filter button shows ARE, in words ("behind", "due this
   * week"), so the button never reads as a bare number: a reader has to know
   * whether it counts what is left or what is done.
   */
  shortfallLabel: string
  /**
   * Where this goal's dotted line sits, and the bar lights up at or above it.
   * Per goal since 2026-10-21 -- see the file docblock.
   */
  targetPct: number
}

export const FORECAST_GOALS: ReadonlyArray<ForecastGoal> = [
  {
    key: 'BOOKED_WITH_ROOM',
    label: 'Booked with room',
    description:
      'Booked cases whose client meeting is more than a week away. Booking further ahead fills this.',
    shortfallLabel: 'booked inside a week',
    targetPct: 90,
  },
  {
    key: 'STARTED_IN_TIME',
    label: 'Started in time',
    description:
      'Booked cases still on or before their latest safe start. Starting work sooner fills this; falling behind empties it.',
    shortfallLabel: 'booked and behind',
    targetPct: GOAL_TARGET_PCT,
  },
]

export interface ForecastGoalReading {
  key: ForecastGoalKey
  /** Cases meeting the goal. */
  met: number
  /** Booked cases in scope. */
  total: number
  /**
   * Booked cases NOT meeting the goal: `total - met`. The one source for the
   * number on the goal's filter button, so the button can only ever say how
   * many rows selecting it produces (see `forecast-goal-filter.ts`).
   */
  shortfall: number
  /** 0-100, rounded. Null when nothing is in scope — not zero. */
  pct: number | null
  /** The goal's own target, so the bar draws and words it from the reading. */
  targetPct: number
  /** At or above {@link targetPct}: the bar is lit. */
  onTarget: boolean
}

/**
 * Both readings, from the open queue rows the page already has.
 *
 * **`pct` is null rather than 0 for an empty board.** Zero is a claim — "we
 * are failing" — and an empty list is not failing at anything. The same
 * distinction `budgetBusinessDaysLeft` and the tab counts already draw.
 */
export function forecastGoalReadings(
  rows: ReadonlyArray<StatusPillRow>,
  now: Date,
): Array<ForecastGoalReading> {
  const booked = rows.filter(isBooked)
  const reading = (key: ForecastGoalKey, met: number): ForecastGoalReading => {
    const { targetPct } = FORECAST_GOALS.find((goal) => goal.key === key)!
    const pct =
      booked.length === 0 ? null : Math.round((met / booked.length) * 100)
    return {
      key,
      met,
      total: booked.length,
      shortfall: booked.length - met,
      pct,
      targetPct,
      onTarget: pct !== null && pct >= targetPct,
    }
  }
  return [
    reading(
      'BOOKED_WITH_ROOM',
      booked.filter((row) => !isDueThisWeek(row, now)).length,
    ),
    reading(
      'STARTED_IN_TIME',
      booked.filter((row) => !isBehindSchedule(row, now)).length,
    ),
  ]
}
